import React, { createContext, useCallback, useContext, useMemo, useReducer, useState } from 'react';
import { Loader, LoaderOptions } from '@googlemaps/js-api-loader';

import { Branch, GetDistances, LatLng } from 'src/components/GoogleMaps/GoogleMapsTypes';

import * as googleMapsApi from './googleMapsApi';

enum ActionTypes {
    ApiNotLoaded = 'Api Not Loaded',
    APILoaded = 'API Loaded',
    APILoadFailed = 'API Load Failed',
    APILoading = 'API Loading',
    SetApiVersion = 'Set API Version',
    SetGeocoder = 'Set Geocoder',
}

interface Actions {
    apiNotLoaded(): { payload: any; type: string };
    apiLoaded(): { payload: any; type: string };
    apiLoadFailed(error: any): { payload: { error: any }; type: string };
    apiLoading(): { payload: any; type: string };
    setApiVersion(version: string): { payload: { version: string }; type: string };
}

const actions: Actions = {
    apiNotLoaded: () => ({
        payload: {},
        type: ActionTypes.ApiNotLoaded,
    }),
    apiLoaded: () => ({
        payload: {},
        type: ActionTypes.APILoaded,
    }),
    apiLoadFailed: (error: string) => ({
        payload: {
            error,
        },
        type: ActionTypes.APILoadFailed,
    }),
    apiLoading: () => ({
        payload: {},
        type: ActionTypes.APILoading,
    }),
    setApiVersion: (version: string) => ({
        payload: { version },
        type: ActionTypes.SetApiVersion,
    }),
};

enum GoogleMapsApiStatus {
    Error = 'Error',
    Loading = 'Loading',
    NotLoaded = 'Not loaded',
    Ready = 'Ready',
}

interface GoogleMapsApi {
    /**
     * Options passed to `loader`
     */
    options: LoaderOptions;

    /**
     * Google Maps API status
     */
    status: string;

    /**
     * Google Maps API status
     */
    version: string | null;
}

interface GoogleMapsApiContext {
    api: GoogleMapsApi;
    apiIsLoading: boolean;
    apiIsReady: boolean;
    error: any | null;
    getAddressCoordinates(address: string): Promise<LatLng> | any;
    loadGoogleMapsApi(): Promise<void> | any;
    sortByDistance(lat: number, lng: number, locations: Branch[]): GetDistances | any;
}

type ExposedLoaderOptions = Pick<LoaderOptions, 'apiKey' | 'language' | 'libraries' | 'region'>;

interface GoogleMapsApiProviderProps extends ExposedLoaderOptions {
    children?: any;
}

const initialState: GoogleMapsApiContext = {
    api: {
        options: {
            apiKey: '',
            id: 'google-maps-api',
            libraries: ['geometry'],
        },
        status: GoogleMapsApiStatus.NotLoaded,
        version: null,
    },
    apiIsLoading: false,
    apiIsReady: false,
    error: null,
    getAddressCoordinates: () => new Promise((reject) => reject('Function is not yet implemented')),
    loadGoogleMapsApi: () => new Promise((reject) => reject('Function is not yet implemented')),
    sortByDistance: () => null,
};

/**
 * Context used by `GoogleMaps` & children.
 */
export const GoogleMapsApiContext = createContext<GoogleMapsApiContext>(initialState);

const reducer = (state, { payload, type }) => {
    const newState = {
        ...state,
    };

    switch (type) {
        case ActionTypes.ApiNotLoaded:
            newState.api.status = GoogleMapsApiStatus.NotLoaded;
            newState.apiIsReady = false;
            newState.apiIsLoading = false;
            newState.error = null;
            break;

        case ActionTypes.APILoadFailed:
            newState.api.status = GoogleMapsApiStatus.Error;
            newState.apiIsReady = false;
            newState.apiIsLoading = false;
            newState.error = payload.error;
            break;

        case ActionTypes.APILoaded:
            newState.api.status = GoogleMapsApiStatus.Ready;
            newState.apiIsReady = true;
            newState.apiIsLoading = false;
            break;

        case ActionTypes.APILoading:
            newState.api.status = GoogleMapsApiStatus.Loading;
            newState.apiIsReady = false;
            newState.apiIsLoading = true;
            break;

        case ActionTypes.SetApiVersion:
            newState.api.version = payload.version;
            break;

        case ActionTypes.SetGeocoder:
            newState.geocoder = payload.geocoder;
            break;

        default:
            break;
    }

    return newState;
};

export function GoogleMapsApiProvider(props: GoogleMapsApiProviderProps) {
    const { apiKey, children, language, libraries, region } = props;
    const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>();

    const [state, dispatch] = useReducer(
        reducer,
        {
            apiKey: apiKey ?? initialState.api.options.apiKey,
            language: language ?? initialState.api.options.language,
            libraries: libraries ?? [],
            region: region ?? initialState.api.options.region,
        },
        (options: ExposedLoaderOptions) => {
            const newState = {
                ...initialState,
            };

            // Ensure optional types fallback to an empty array.
            const defaultLibraries = initialState.api.options.libraries ?? [];
            const optionsLibraries = options.libraries ?? [];

            /**
             * Merge default library options with the custom
             * libraries input.
             *
             * By using a `Set`, we remove multiple occurrences
             * of a library from the array.
             */
            const libraries = [...new Set([...defaultLibraries, ...optionsLibraries])];

            newState.api.options = {
                ...initialState.api.options,
                apiKey: options.apiKey,
                language: options.language,
                libraries: libraries,
                region: options.region,
            };

            return newState;
        },
    );

    const {
        api: { options: apiOptions, status: apiStatus },
    } = state;

    const loadGoogleMapsApi = useCallback(() => {
        return new Promise<void>((resolve, reject) => {
            if (apiStatus === GoogleMapsApiStatus.Ready) {
                return resolve();
            }

            if (apiStatus === GoogleMapsApiStatus.Error) {
                dispatch(actions.apiNotLoaded());
            }

            dispatch(actions.apiLoading());

            const loader = new Loader(apiOptions);

            loader
                .load()
                .then(() => {
                    dispatch(actions.setApiVersion(google.maps.version));
                    dispatch(actions.apiLoaded());
                    resolve();
                })
                .catch((error) => {
                    dispatch(actions.apiLoadFailed(error));
                    reject(error);
                });
        });
    }, [apiOptions, apiStatus, dispatch]);

    const getGeocoder = useCallback(() => {
        if (geocoder) {
            return geocoder;
        } else {
            const geocoderInstance = new google.maps.Geocoder();
            Object.freeze(geocoderInstance);

            setGeocoder(geocoderInstance);

            return geocoderInstance;
        }
    }, [geocoder]);

    const getAddressCoordinates = useCallback(
        (address: string) =>
            loadGoogleMapsApi().then(() =>
                googleMapsApi.getAddressCoordinates({
                    address,
                    geocoder: getGeocoder(),
                }),
            ),
        [getGeocoder, loadGoogleMapsApi],
    );

    const sortByDistance = useCallback((lat, lng, locations) => {
        return googleMapsApi.sortByDistance({ lat, lng, locations });
    }, []);

    const contextValue = useMemo(
        () => ({
            ...state,
            loadGoogleMapsApi,
            getAddressCoordinates,
            sortByDistance,
        }),
        [state, loadGoogleMapsApi, getAddressCoordinates, sortByDistance],
    );

    return <GoogleMapsApiContext.Provider value={contextValue}>{children ?? children}</GoogleMapsApiContext.Provider>;
}

GoogleMapsApiProvider.displayName = 'GoogleMapsApi.Provider';

export function useGoogleMapsApi() {
    const context = useContext(GoogleMapsApiContext);

    if (context === undefined) {
        throw new Error('useGoogleMapsApi must be used within a GoogleMapsProvider');
    }

    return context;
}
